import { FormEvent, useEffect, useRef, useState } from "react"
import GameScreenLayout from "@/components/GameScreenLayout"
import { redeemBooster, redeemCard } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { parseRedeemPayload } from "@/utils/redeemPayload"
import { BoosterRedeemType } from "@/utils/redeemPayload"

export default function RedeemPage() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [raw, setRaw] = useState("")
  const [scanning, setScanning] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const { syncGameState, bumpCards } = useGuardiansPassport()

  useEffect(() => {
    return () => {
      setScanning(false)
    }
  }, [])

  async function redeemParsed(code: string) {
    const parsed = parseRedeemPayload(code)
    if (!parsed) {
      setError("Неизвестный формат кода или QR")
      return
    }
    setLoading(true)
    setError(null)
    setMessage(null)
    try {
      if (parsed.kind === "card") {
        const res = await redeemCard(parsed.code)
        setMessage(`Карта активирована: ${res.card.element} Lv.${res.card.level}`)
        bumpCards()
      } else {
        const res = await redeemBooster(parsed.kind as BoosterRedeemType, parsed.code)
        setMessage(
          res.passport
            ? `Бустер «${parsed.kind}» активирован`
            : `Бустер «${parsed.kind}» активирован`
        )
      }
      await syncGameState()
      setRaw("")
    } catch (e) {
      setError(getGameApiErrorMessage(e, "Активация не удалась"))
    } finally {
      setLoading(false)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void redeemParsed(raw)
  }

  async function startCameraScan() {
    setError(null)
    try {
      const { BrowserQRCodeReader } = await import("@zxing/browser")
      const reader = new BrowserQRCodeReader()
      setScanning(true)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      })
      const video = videoRef.current
      if (video) {
        video.srcObject = stream
        await video.play()
      }
      const result = await reader.decodeOnceFromVideoDevice(undefined, videoRef.current!)
      stream.getTracks().forEach((t) => t.stop())
      setScanning(false)
      if (result.getText()) {
        setRaw(result.getText())
        await redeemParsed(result.getText())
      }
    } catch (e) {
      setScanning(false)
      setError(
        getGameApiErrorMessage(
          e,
          "Камера недоступна — введите код вручную или установите @zxing/browser"
        )
      )
    }
  }

  return (
    <GameScreenLayout title="QR / Redeem" backTo="/play/collection">
      <p className="mb-4 text-xs text-guardians-muted">
        Код карты CARD-… или бустера BOOSTER-…, либо URL из QR.
      </p>

      <form onSubmit={onSubmit} className="space-y-3">
        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={3}
          placeholder="Вставьте код или результат скана"
          className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={loading || !raw.trim()}
          className="w-full rounded-xl bg-guardians-accent py-3 text-sm font-medium text-guardians-bg disabled:opacity-40"
        >
          {loading ? "Активация…" : "Активировать"}
        </button>
      </form>

      <button
        type="button"
        disabled={scanning || loading}
        onClick={() => void startCameraScan()}
        className="mt-3 w-full rounded-xl border border-white/20 py-2 text-sm"
      >
        {scanning ? "Сканирование…" : "Сканировать камерой"}
      </button>

      <video
        ref={videoRef}
        className={scanning ? "mt-3 aspect-video w-full rounded-xl bg-black" : "hidden"}
        muted
        playsInline
      />

      {message ? (
        <p className="mt-3 text-sm text-green-300">{message}</p>
      ) : null}
      {error ? <p className="mt-3 text-sm text-red-300">{error}</p> : null}
    </GameScreenLayout>
  )
}
